/**
 * Music director: owns the two looping Sequencers (menu theme, in-game
 * ambient) plus the tension layer and stingers, and the screen-driven state
 * machine that starts/stops them.
 */

import { ensureGraph } from './context';
import { noteToFreq, noteDurationSteps, isRest } from './notes';
import { Sequencer } from './scheduler';
import { playTone } from './synth';
import {
  GAME_ARP_VARIANTS,
  GAME_BARS_PER_PHRASE,
  GAME_BASS,
  GAME_BAR_ROOTS,
  GAME_BPM,
  GAME_HAT_STEPS,
  GAME_STEPS_PER_BAR,
  MENU_BASS,
  MENU_BPM,
  MENU_LEAD,
  MENU_STEPS_PER_BAR,
  STINGER_DEFEAT,
  STINGER_PHASE_UP,
  STINGER_VICTORY,
  TENSION_HAT_STEPS,
  TENSION_PULSE_STEPS,
  type StingerNote,
} from './patterns';
import { playNoise } from './synth';

export type Screen = 'menu' | 'game' | 'none';

/** Exposed for the (temporary, removed-before-ship) verification harness —
 * incremented every time a note/hat is actually scheduled, so a headless
 * test can assert music is really running without touching real audio. */
export interface AudioDebugCounters {
  notesScheduled: number;
  activeTrack: Screen;
  tensionActive: boolean;
}

export const debugCounters: AudioDebugCounters = {
  notesScheduled: 0,
  activeTrack: 'none',
  tensionActive: false,
};

let screen: Screen = 'none';
let menuSeq: Sequencer | null = null;
let gameSeq: Sequencer | null = null;

// Tension layer state
let tensionGain: GainNode | null = null;
let tensionActive = false;
let lastUnderAttackAt = 0;
let lastBudget = 0;

// Ambient ducking (for stingers)
let ambientDuckGain: GainNode | null = null;

// Arp variant rotation (game loop): pseudo-random, never the same twice.
let arpVariantIdx = 0;

function pickNextVariant(count: number, current: number): number {
  if (count <= 1) return 0;
  let next = current;
  while (next === current) {
    next = Math.floor(Math.random() * count);
  }
  return next;
}

function scheduleGridVoice(
  ctx: BaseAudioContext,
  dest: AudioNode,
  pattern: readonly string[],
  step: number,
  time: number,
  stepDurSec: number,
  shape: 'square' | 'triangle',
  opts: { gain: number; duty?: number },
): void {
  const i = step % pattern.length;
  const token = pattern[i];
  if (isRest(token)) return;
  // '.' (hold) tokens are consumed by the note that started them; skip.
  if (token === '.') return;
  const freq = noteToFreq(token);
  if (freq === null) return;
  const steps = noteDurationSteps(pattern, i);
  const dur = steps * stepDurSec * 0.92; // slight gap so notes don't smear
  playTone(ctx, dest, time, freq, dur, shape, opts);
  debugCounters.notesScheduled++;
}

function startMenu(): void {
  const { ctx, musicBus } = ensureGraph();
  const seq = new Sequencer(ctx, MENU_STEPS_PER_BAR, () => MENU_BPM, (step, time, dur) => {
    scheduleGridVoice(ctx, musicBus, MENU_BASS, step, time, dur, 'triangle', { gain: 0.11 });
    scheduleGridVoice(ctx, musicBus, MENU_LEAD, step, time, dur, 'square', { gain: 0.075, duty: 0.28 });
  });
  seq.start();
  menuSeq = seq;
  debugCounters.activeTrack = 'menu';
}

function stopMenu(): void {
  menuSeq?.stop();
  menuSeq = null;
}

function startGame(): void {
  const graph = ensureGraph();
  const { ctx, musicBus } = graph;

  // Dedicated bus for the tension layer so it can crossfade independently.
  tensionGain = ctx.createGain();
  tensionGain.gain.value = 0;
  tensionGain.connect(musicBus);

  // A sub-bus for the ambient loop itself so stingers can duck just this.
  ambientDuckGain = ctx.createGain();
  ambientDuckGain.gain.value = 1;
  ambientDuckGain.connect(musicBus);

  arpVariantIdx = Math.floor(Math.random() * GAME_ARP_VARIANTS.length);
  tensionActive = false;
  lastUnderAttackAt = 0;
  lastBudget = 0;

  const stepsPerPhrase = GAME_STEPS_PER_BAR * GAME_BARS_PER_PHRASE;

  const seq = new Sequencer(ctx, GAME_STEPS_PER_BAR, () => GAME_BPM, (step, time, dur) => {
    const dest = ambientDuckGain!;
    // Bass: quiet triangle, loops every 4-bar phrase.
    scheduleGridVoice(ctx, dest, GAME_BASS, step, time, dur, 'triangle', { gain: 0.09 });

    // Arp: rotate variant at the top of each 4-bar phrase.
    const posInPhrase = step % stepsPerPhrase;
    if (posInPhrase === 0 && step > 0) {
      arpVariantIdx = pickNextVariant(GAME_ARP_VARIANTS.length, arpVariantIdx);
    }
    const variant = GAME_ARP_VARIANTS[arpVariantIdx];
    scheduleGridVoice(ctx, dest, variant, posInPhrase, time, dur, 'square', { gain: 0.055, duty: 0.15 });

    // Sparse hat: only on odd bars (every other bar), soft.
    const barIndex = Math.floor(posInPhrase / GAME_STEPS_PER_BAR);
    const stepInBar = posInPhrase % GAME_STEPS_PER_BAR;
    if (barIndex % 2 === 1 && GAME_HAT_STEPS.includes(stepInBar)) {
      playNoise(ctx, dest, time, 0.06, { gain: 0.04, filterHz: 6000, filterType: 'highpass', release: 0.05 });
      debugCounters.notesScheduled++;
    }

    // Tension layer: always scheduled (into its own gain, ramped by
    // updateTension), so it *layers* in rather than switching tracks.
    if (tensionGain && TENSION_PULSE_STEPS.includes(stepInBar)) {
      const root = GAME_BAR_ROOTS[barIndex % GAME_BAR_ROOTS.length];
      const freq = noteToFreq(root);
      if (freq !== null) {
        playTone(ctx, tensionGain, time, freq / 2, dur * 0.5, 'square', { gain: 0.14, duty: 0.5 });
        debugCounters.notesScheduled++;
      }
    }
    if (tensionGain && TENSION_HAT_STEPS.includes(stepInBar)) {
      playNoise(ctx, tensionGain, time, 0.045, { gain: 0.05, filterHz: 7500, filterType: 'highpass', release: 0.035 });
    }
  });
  seq.start();
  gameSeq = seq;
  debugCounters.activeTrack = 'game';
}

function stopGame(): void {
  gameSeq?.stop();
  gameSeq = null;
  tensionGain?.disconnect();
  tensionGain = null;
  ambientDuckGain?.disconnect();
  ambientDuckGain = null;
  tensionActive = false;
}

export function setScreen(next: Screen): void {
  if (next === screen) return;
  if (screen === 'menu') stopMenu();
  if (screen === 'game') stopGame();
  screen = next;
  if (next === 'menu') startMenu();
  if (next === 'game') startGame();
  if (next === 'none') debugCounters.activeTrack = 'none';
}

export function getScreen(): Screen {
  return screen;
}

/** Called every sim tick while in-game with the local player's current
 * budget; drives the tension-layer crossfade. */
export function updateTensionBudget(budget: number): void {
  lastBudget = budget;
  reconcileTension();
}

export function notifyUnderAttack(): void {
  lastUnderAttackAt = Date.now();
  reconcileTension();
}

function reconcileTension(): void {
  if (!tensionGain) return;
  const shouldBeActive = lastBudget <= 0 || Date.now() - lastUnderAttackAt < 8000;
  if (shouldBeActive === tensionActive) return;
  tensionActive = shouldBeActive;
  debugCounters.tensionActive = tensionActive;
  const { ctx } = ensureGraph();
  const now = ctx.currentTime;
  tensionGain.gain.cancelScheduledValues(now);
  const current = tensionGain.gain.value;
  tensionGain.gain.setValueAtTime(Math.max(current, 0.0001), now);
  tensionGain.gain.linearRampToValueAtTime(shouldBeActive ? 0.9 : 0.0001, now + 1.5);
}

// ---------------------------------------------------------------------------
// Stingers
// ---------------------------------------------------------------------------

function playStingerNotes(dest: AudioNode, notes: StingerNote[]): number {
  const { ctx } = ensureGraph();
  const start = ctx.currentTime + 0.02;
  let maxEnd = 0;
  for (const n of notes) {
    const freq = noteToFreq(n.note);
    if (freq === null) continue;
    playTone(ctx, dest, start + n.at, freq, n.dur, n.shape, { gain: n.gain, duty: n.duty });
    debugCounters.notesScheduled++;
    maxEnd = Math.max(maxEnd, n.at + n.dur);
  }
  return maxEnd;
}

function duckAmbient(durationSec: number): void {
  if (!ambientDuckGain) return;
  const { ctx } = ensureGraph();
  const now = ctx.currentTime;
  const g = ambientDuckGain.gain;
  g.cancelScheduledValues(now);
  g.setValueAtTime(g.value, now);
  g.linearRampToValueAtTime(0.32, now + 0.15);
  g.setValueAtTime(0.32, now + Math.max(0.15, durationSec));
  g.linearRampToValueAtTime(1, now + Math.max(0.15, durationSec) + 0.4);
}

export function playVictoryStinger(): void {
  const { musicBus } = ensureGraph();
  const dur = playStingerNotes(musicBus, STINGER_VICTORY);
  duckAmbient(dur);
}

export function playDefeatStinger(): void {
  const { musicBus } = ensureGraph();
  const dur = playStingerNotes(musicBus, STINGER_DEFEAT);
  duckAmbient(dur);
}

export function playPhaseUpStinger(): void {
  const { musicBus } = ensureGraph();
  const dur = playStingerNotes(musicBus, STINGER_PHASE_UP);
  duckAmbient(dur);
}
